import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import authService from '../services/auth.service';
import oauthService from '../services/oauth.service';
import CompanyBanner from '../components/company/CompanyBanner';
import axiosInstance from '../config/axios.config';
import Swal from 'sweetalert2';

// Constants
const TOAST_DURATIONS = {
    ERROR: 5000
};

const Login = () => {
    const navigate = useNavigate();
    const toast = useRef(null);
    const [searchParams] = useSearchParams();
    const companyToken = searchParams.get('company');
    const advisorToken = searchParams.get('advisor'); // MLM Phase 2: Preserve advisor token

    // Helper: Build URL with preserved tokens
    const buildAuthUrl = (path) => {
        const params = [];
        if (companyToken) params.push(`company=${companyToken}`);
        if (advisorToken) params.push(`advisor=${advisorToken}`);
        return params.length > 0 ? `${path}?${params.join('&')}` : path;
    };

    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [oauthLoading, setOauthLoading] = useState(false);
    const [passwordVisible, setPasswordVisible] = useState(false);

    // Company state
    const [companyInfo, setCompanyInfo] = useState(null);
    const [loadingCompany, setLoadingCompany] = useState(false);
    const [companyError, setCompanyError] = useState(null);

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: value
        }));
        
        // Real-time validation
        const newErrors = { ...errors };
        
        if (id === 'email') {
            if (value && !/\S+@\S+\.\S+/.test(value)) {
                newErrors.email = 'Please enter a valid email address.';
            } else {
                delete newErrors.email;
            }
        }
        
        if (id === 'password') {
            if (value) {
                delete newErrors.password;
            }
        }
        
        // Clear general error when user starts typing
        delete newErrors.general;
        
        setErrors(newErrors);
    };

    // Fetch company info if token is present
    useEffect(() => {
        if (companyToken) {
            fetchCompanyInfo(companyToken);
        }
    }, [companyToken]);

    const fetchCompanyInfo = async (token) => {
        setLoadingCompany(true);
        setCompanyError(null);
        try {
            const response = await axiosInstance.get(`/register/company-info?token=${token}`);

            if (response.data.success && response.data.valid) {
                setCompanyInfo({
                    id: response.data.company_id,
                    name: response.data.company_name,
                    email: response.data.company_email
                });
            } else {
                setCompanyError('Invalid or expired registration link');
            }
        } catch (error) {
            setCompanyError(error.response?.data?.message || 'Failed to verify company link');
        } finally {
            setLoadingCompany(false);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address.';
        }
        if (!formData.password) {
            newErrors.password = 'Password field is required.';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    // Fetch CSRF cookie on mount so XSRF-TOKEN cookie is present for all subsequent requests.
    // Uses an absolute path (not the /backend/api baseURL) to hit the Sanctum endpoint directly.
    useEffect(() => {
        fetch('/sanctum/csrf-cookie', { credentials: 'include' }).catch(() => {
            // Non-fatal — Bearer token auth works without CSRF cookie
        });
    }, []);

    // Check for authentication on mount
    useEffect(() => {
        const token = localStorage.getItem('authToken');
        if (token) {
            navigate('/app/dashboard');
        }
    }, [navigate]);

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        setErrors({});

        try {
            const data = await authService.login(formData.email, formData.password);

            if (data.success) {
                // Show simple success message
                Swal.fire({
                    title: 'Login Successful!',
                    text: 'Welcome back to INTERESTNG',
                    icon: 'success',
                    timer: 2000,
                    timerProgressBar: true,
                    showConfirmButton: false,
                    allowOutsideClick: false,
                    allowEscapeKey: false
                });

                // Close SweetAlert and navigate after short delay
                setTimeout(() => {
                    Swal.close();
                    navigate('/app/dashboard');
                }, 1800);
            }
        } catch (error) {
            // Log error for debugging (in development only)
            if (process.env.NODE_ENV === 'development') {
                console.log('Login error:', error);
            }
            let errorMessage = error.response?.data?.message || error.message || 'Login failed. Please check your credentials.';
            
            // Clean up error messages - remove generic messages and formatting
            if (errorMessage.toLowerCase().includes('the provided credentials are incorrect') ||
                errorMessage.toLowerCase().includes('provided credentials are incorrect')) {
                errorMessage = 'invalid credentials';
            }
            
            // Remove any :: characters and trim whitespace
            errorMessage = errorMessage.replace(/^::?\s*/, '').replace(/\s*::?\s*/g, ' ').trim();
            
            // Check for specific backend error responses
            const errorCode = error.response?.status;
            const errorData = error.response?.data;
            
            // Handle different types of authentication errors
            if (errorCode === 401) {
                // Check if it's an email not found error (only email wrong)
                if (errorMessage.toLowerCase().includes('user not found') ||
                    errorMessage.toLowerCase().includes('email not found') ||
                    errorMessage.toLowerCase().includes('account not found') ||
                    errorMessage.toLowerCase().includes('no user found')) {
                    setErrors({
                        email: 'This email address is not registered with us. Please check your email or create a new account.'
                    });
                } 
                // Check if it's a password mismatch error (only password wrong)
                else if (errorMessage.toLowerCase().includes('incorrect password') ||
                         errorMessage.toLowerCase().includes('wrong password') ||
                         errorMessage.toLowerCase().includes('invalid password')) {
                    setErrors({
                        password: 'The password you entered is incorrect. Please check your password and try again.'
                    });
                }
                // Generic invalid credentials - show specific messages for both fields
                else if (errorMessage.toLowerCase().includes('invalid credentials') ||
                         errorMessage.toLowerCase().includes('provided credentials are incorrect') ||
                         errorMessage.toLowerCase().includes('credentials are incorrect') ||
                         errorMessage.toLowerCase().includes('unauthorized') ||
                         errorMessage.toLowerCase().includes('authentication failed') ||
                         errorMessage.toLowerCase().includes('login failed')) {
                    // Show specific error messages for both fields
                    setErrors({
                        email: 'Please double-check your email address.',
                        password: 'Please double-check your password.'
                    });
                }
                else {
                    // Fallback - show errors on both fields for unknown auth errors
                    setErrors({
                        email: 'Please verify your email address.',
                        password: 'Please verify your password.'
                    });
                }
            } 
            // Handle validation errors for email format
            else if (errorMessage.toLowerCase().includes('email')) {
                setErrors({
                    email: errorMessage
                });
            } 
            // Handle other errors as general
            else {
                setErrors({
                    general: errorMessage || 'Unable to sign you in at this moment. Please try again shortly.'
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setOauthLoading(true);

        try {
            // Pass both company_token and advisor_token (if present)
            const result = await oauthService.initiateGoogleOAuth(companyToken, advisorToken);

            if (result.success) {
                // Redirect to Google OAuth URL
                window.location.href = result.oauth_url;
            } else {
                // Show error if failed
                setErrors({
                    general: result.message || 'Google Sign-In is temporarily unavailable. Please try again or sign in with your email.'
                });
                setOauthLoading(false);
            }
        } catch (error) {
            // Log error for debugging (in development only)
            if (process.env.NODE_ENV === 'development') {
                console.error('OAuth error:', error);
            }
            setErrors({
                general: 'Google Sign-In service is temporarily unavailable. Please try again later or sign in with your email.'
            });
            setOauthLoading(false);
        }
    };

    return (
        <>
            <Toast ref={toast} />
            <div className="flex min-h-screen">
                {/* Left Panel - Form Section */}
                <div className="w-full lg:w-4 min-h-screen lg:h-screen text-center px-4 py-4 lg:px-6 lg:py-6 flex flex-column justify-content-center lg:justify-content-between surface-ground overflow-y-auto">
                    {/* Logo - Hidden on mobile, shown on desktop */}
                    <img src={`${process.env.PUBLIC_URL}/layout/images/interestng_logo_updated.jpeg`} alt="INTERESTNG" className="mx-auto hidden lg:block" style={{ height: '3rem', width: 'auto', mixBlendMode: 'multiply', filter: 'contrast(1.1) brightness(1.05)' }} />

                    <div className="flex flex-column align-items-center gap-3 lg:gap-4">
                        {/* Logo - Shown on mobile only */}
                        <img src={`${process.env.PUBLIC_URL}/layout/images/interestng_logo_updated.jpeg`} alt="INTERESTNG" className="block lg:hidden mb-3" style={{ height: '3rem', width: 'auto', mixBlendMode: 'multiply', filter: 'contrast(1.1) brightness(1.05)' }} />

                        <div className="mb-2 lg:mb-3">
                            <h2 className="text-xl lg:text-2xl">Login to your account</h2>
                            <p>
                                Forgot password? <Link to={buildAuthUrl('/app/forgot-password')} className="text-primary hover:underline cursor-pointer font-medium">Click here</Link> to reset.
                            </p>
                        </div>

                        {/* Company Banner - Show if company info is valid */}
                        {companyInfo && !loadingCompany && (
                            <div className="w-full max-w-25rem mb-2">
                                <CompanyBanner companyName={companyInfo.name} />
                            </div>
                        )}

                        {/* Loading Company Info */}
                        {loadingCompany && (
                            <div className="w-full max-w-25rem mb-2">
                                <Message severity="info" text="Verifying company registration link..." />
                            </div>
                        )}

                        {/* Company Error */}
                        {companyError && (
                            <div className="w-full max-w-25rem mb-2">
                                <Message severity="warn" text={companyError} />
                            </div>
                        )}

                        <div className="flex flex-column gap-4 w-full max-w-25rem">
                            {errors.general && (
                                <small className="p-error">{errors.general}</small>
                            )}
                            
                            <span className="p-input-icon-left">
                                <i className="pi pi-envelope" />
                                <InputText
                                    id="email"
                                    type="email"
                                    placeholder="Email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    disabled={loading || oauthLoading}
                                    className="w-full"
                                    autoComplete="username email"
                                    aria-invalid={!!errors.email}
                                    aria-describedby={errors.email ? "email-error" : undefined}
                                />
                            </span>
                            {errors.email && <small id="email-error" className="p-error">{errors.email}</small>}
                            
                            <span className="p-input-icon-left p-input-icon-right">
                                <i className="pi pi-lock" />
                                <i 
                                    className={`pi ${passwordVisible ? 'pi-eye-slash' : 'pi-eye'} cursor-pointer`}
                                    onClick={() => setPasswordVisible(!passwordVisible)}
                                    aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setPasswordVisible(!passwordVisible);
                                        }
                                    }}
                                />
                                <InputText
                                    id="password"
                                    type={passwordVisible ? "text" : "password"}
                                    placeholder="Password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    disabled={loading || oauthLoading}
                                    className="w-full"
                                    autoComplete="off"
                                    aria-invalid={!!errors.password}
                                    aria-describedby={errors.password ? "password-error" : undefined}
                                />
                            </span>
                            {errors.password && <small id="password-error" className="p-error">{errors.password}</small>}
                            
                            <Button 
                                label="LOGIN" 
                                className="w-full"
                                onClick={handleLogin}
                                loading={loading}
                                disabled={loading || oauthLoading}
                            />
                            
                            <div className="flex align-items-center w-full mb-3">
                                <div className="border-top-1 surface-border flex-auto"></div>
                                <span className="px-3 text-color-secondary">or</span>
                                <div className="border-top-1 surface-border flex-auto"></div>
                            </div>
                            
                            <Button
                                className="w-full mb-4 p-button-outlined"
                                type="button"
                                onClick={handleGoogleLogin}
                                loading={oauthLoading}
                                disabled={loading}
                            >
                                <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    gap: '12px',
                                    width: '100%',
                                    textAlign: 'center'
                                }}>
                                    <svg width="20" height="20" viewBox="0 0 24 24">
                                        <path fill="#4285f4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                        <path fill="#34a853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                        <path fill="#fbbc05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                        <path fill="#ea4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                    </svg>
                                    <span style={{ 
                                        color: '#3c4043', 
                                        fontSize: '14px', 
                                        fontWeight: '500',
                                        textAlign: 'center',
                                        display: 'block'
                                    }}>Continue with Google</span>
                                </div>
                            </Button>
                            
                            <span className="font-semibold text-color-secondary">
                                Don't have an account? <Link to={buildAuthUrl('/app/register')} className="font-semibold cursor-pointer primary-color">Sign up</Link>
                            </span>
                        </div>
                    </div>

                </div>

                {/* Right Panel - Hero Section */}
                <div className="w-8 hidden lg:flex flex-column justify-content-center align-items-center px-6 py-6 auth-hero-section">
                    <div className="text-center">
                        <span className="block text-white text-7xl font-bold mb-4">
                            INTERESTNG
                        </span>
                        <span className="block text-white text-5xl font-semibold mb-4">
                            Account
                        </span>
                        <span className="block text-white text-xl line-height-3">
                            Comprehensive credit reports and analysis
                            <br />at your fingertips. Secure, fast, and reliable
                            <br />financial data management.
                        </span>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Login;
